import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../context/GameContext.jsx';
import { useAuth } from '../context/AuthContext.jsx';
import api from '../api.js';
import FmCard from '../components/FmCard.jsx';
import { YELLOW_CARD, RED_CARD } from '../data/penalties.js';

const HOLE_OPTIONS = [3, 6, 9];
const TRANSPORT = [
  { id: 'walking', label: 'Walking', icon: '🚶' },
  { id: 'driving', label: 'Driving / Uber', icon: '🚗' },
];
const GROUP_SIZES = [
  { id: 'small', label: 'Small (2–4)' },
  { id: 'medium', label: 'Medium (5–8)' },
  { id: 'large', label: 'Large (9+)' },
];
const START_TIMES = [
  { id: '6pm', label: '6 PM' },
  { id: '7pm', label: '7 PM' },
  { id: '8pm', label: '8 PM' },
  { id: '9pm+', label: '9 PM+' },
];
const DRINKS = [
  { id: 'mixed', label: 'Mixed', icon: '🍸' },
  { id: 'beer', label: 'Beer', icon: '🍺' },
  { id: 'cocktails', label: 'Cocktails', icon: '🍹' },
  { id: 'shots', label: 'Shots', icon: '🥃' },
];
const BUDGETS = [
  { id: 'cheap', label: 'Budget-Friendly', icon: '💵' },
  { id: 'any', label: 'Normal', icon: '💰' },
  { id: 'nolimit', label: 'No Limit', icon: '💎' },
];

const newId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2)
  ).slice(0, 8);

export default function GameSetup() {
  const { draft, updateDraft, resetDraft } = useGame();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Holds the freshly generated/created course so the user can preview
  // it and tap "Start Round" as a separate, deliberate step.
  const [readyCourse, setReadyCourse] = useState(null);
  const [premiumGate, setPremiumGate] = useState(false);

  const set = (k) => (v) => updateDraft({ [k]: v });

  // ===== Teams =====
  const teams = draft.teams || [];

  const updateTeam = (id, patch) => {
    updateDraft({
      teams: teams.map((t) => (t.id === id ? { ...t, ...patch } : t)),
    });
  };

  const addTeam = () => {
    updateDraft({
      teams: [
        ...teams,
        { id: newId(), name: `Team ${teams.length + 1}`, players: [] },
      ],
    });
  };

  const removeTeam = (id) => {
    if (teams.length <= 1) return;
    updateDraft({ teams: teams.filter((t) => t.id !== id) });
  };

  // Build a fully-shaped player object. The Scorecard expects every
  // player to be an OBJECT with these fields — passing raw strings
  // caused the "Cannot create property 'scores' on string" crash.
  const makePlayer = (name) => ({
    id: newId(),
    name: String(name || '').trim(),
    scores: [],
    cards: { yellow: [], red: [], cardLog: [] },
    dd: false,
  });

  const setTeamPlayers = (teamId, players) => {
    updateTeam(teamId, { players });
  };

  const updatePlayerName = (teamId, idx, name) => {
    const team = teams.find((t) => t.id === teamId);
    if (!team) return;
    const next = (team.players || []).map((p, i) =>
      i === idx ? { ...p, name: String(name || '') } : p
    );
    setTeamPlayers(teamId, next);
  };

  const removePlayer = (teamId, idx) => {
    const team = teams.find((t) => t.id === teamId);
    if (!team) return;
    const next = [...(team.players || [])];
    next.splice(idx, 1);
    // Always keep at least one (possibly empty) row.
    if (next.length === 0) next.push(makePlayer(''));
    setTeamPlayers(teamId, next);
  };

  const appendEmptyPlayer = (teamId) => {
    const team = teams.find((t) => t.id === teamId);
    if (!team) return;
    setTeamPlayers(teamId, [...(team.players || []), makePlayer('')]);
  };

  // ===== Custom hole builder =====
  const customHoles = draft.customHoles || [];

  const addCustomHole = () => {
    updateDraft({
      customHoles: [
        ...customHoles,
        { id: newId(), name: '', address: '', par: 4, drink: '' },
      ],
    });
  };

  const updateCustomHole = (id, patch) => {
    updateDraft({
      customHoles: customHoles.map((h) =>
        h.id === id ? { ...h, ...patch } : h
      ),
    });
  };

  const removeCustomHole = (id) => {
    updateDraft({ customHoles: customHoles.filter((h) => h.id !== id) });
  };

  const moveCustomHole = (id, dir) => {
    const idx = customHoles.findIndex((h) => h.id === id);
    if (idx < 0) return;
    const target = idx + dir;
    if (target < 0 || target >= customHoles.length) return;
    const next = [...customHoles];
    [next[idx], next[target]] = [next[target], next[idx]];
    updateDraft({ customHoles: next });
  };

  // Custom mode is "ready to start" once at least one hole has a name.
  const customReady =
    draft.mode === 'custom' &&
    customHoles.length > 0 &&
    customHoles.every((h) => h.name.trim().length > 0);

  const aiReady = draft.mode === 'ai' && draft.location.trim().length > 0;

  // Step 1 — generate (AI) or build (custom). Does NOT navigate.
  //
  // Server contract reminder:
  //   POST /api/generate → returns the COURSE only: { holes, city, … }
  //                        no session code is created
  //   POST /api/games    → creates a game session from holes+teams,
  //                        returns { id, sessionCode }
  // Both AI and custom modes need the second call to actually start a
  // round.
  const handleGenerate = async () => {
    setBusy(true);
    setError(null);
    setPremiumGate(false);
    try {
      let holes;
      let city;

      if (draft.mode === 'ai') {
        const payload = { userId: user?.uid, ...draft };
        const generated = await api.generateCourse(payload);
        if (!generated?.holes?.length) {
          throw new Error(
            generated?.error || 'AI returned no holes — try a different city.'
          );
        }
        holes = generated.holes;
        city = generated.city || draft.location;
      } else {
        // Custom mode — hand-built holes from the customHoles draft.
        holes = customHoles.map((h, i) => ({
          name: h.name.trim() || `Hole ${i + 1}`,
          address: h.address.trim() || null,
          par: Number(h.par) || 4,
          drink: h.drink.trim() || null,
          emoji: '🍸',
        }));
        city = draft.location || 'Custom Course';
      }

      // Normalize teams — every player MUST be a full object with the
      // shape the Scorecard expects. Drop blank rows the user never
      // filled in, and coerce any legacy string players that might
      // still be hanging around in localStorage/draft state.
      const cleanTeams = (draft.teams || [])
        .map((t) => ({
          id: t.id || newId(),
          name: (t.name || 'Team').trim() || 'Team',
          players: (t.players || [])
            .map((p) =>
              typeof p === 'string'
                ? makePlayer(p)
                : {
                    id: p.id || newId(),
                    name: String(p.name || '').trim(),
                    scores: Array.isArray(p.scores) ? p.scores : [],
                    cards: p.cards || { yellow: [], red: [], cardLog: [] },
                    dd: !!p.dd,
                  }
            )
            .filter((p) => p.name.length > 0),
        }))
        .filter((t) => t.players.length > 0);

      if (cleanTeams.length === 0) {
        throw new Error('Add at least one player before starting a round.');
      }

      // Create the actual game session (POST /api/games returns
      // { id, sessionCode }). Falls back to a sessionStorage-only LOCAL-
      // code if the server is unreachable so the scorecard still works.
      const firstPlayerName = cleanTeams[0]?.players?.[0]?.name || null;
      const gamePayload = {
        userId: user?.uid,
        hostId: user?.uid,
        holes,
        teams: cleanTeams,
        holeCount: holes.length,
        mysteryMode: draft.mysteryMode,
        caddyMode: draft.caddyMode,
        caddyName: firstPlayerName,
      };

      let code;
      try {
        const res = await api.createGame(gamePayload);
        // Server returns { id, sessionCode } — use sessionCode (not code).
        code = res?.sessionCode || res?.session_code || res?.code;
      } catch (serverErr) {
        console.warn('createGame failed, using local fallback', serverErr);
      }

      if (!code) {
        code = 'LOCAL-' + newId().toUpperCase();
        try {
          sessionStorage.setItem(
            `pubgolf.localGame.${code}`,
            JSON.stringify({ code, holes, teams: cleanTeams })
          );
        } catch {}
      }

      setReadyCourse({ code, holes, city, teams: cleanTeams });
    } catch (err) {
      // Premium gate — show the upsell card instead of the raw API error.
      if (err.code === 'premium_required' || err.status === 403) {
        setPremiumGate(true);
      } else if (err.code === 'limit_reached') {
        setError(
          err.message || 'Monthly generation limit reached. Upgrade to Unlimited.'
        );
      } else {
        setError(err.message || 'Something went wrong');
      }
    } finally {
      setBusy(false);
    }
  };

  // Step 2 — actually start the round (only available once readyCourse exists).
  const handleStartRound = () => {
    if (!readyCourse?.code) return;
    const code = readyCourse.code;
    // Mark this browser as the round's HOST so the Scorecard knows to
    // grant caddy-mode edit controls without going through the
    // ClaimPlayer flow. The server also stores host_id on the games row.
    try {
      localStorage.setItem(
        `pubgolf.host.${code}`,
        JSON.stringify({ userId: user?.uid || null, at: Date.now() })
      );
    } catch {}
    resetDraft();
    setReadyCourse(null);
    navigate(`/game/${code}`);
  };

  return (
    <div className="screen setup-screen">
      <div className="screen-header">
        <button className="back-btn" onClick={() => navigate(-1)}>←</button>
        <h1>{draft.mode === 'ai' ? 'AI Course' : 'Custom Course'}</h1>
        <div className="spacer" />
      </div>

      <div className="setup-grid">
      <div className="setup-col">
      {/* COURSE LENGTH */}
      <section className="setup-section">
        <div className="setup-label">Course Length</div>
        <div className="hole-grid">
          {HOLE_OPTIONS.map((h) => (
            <button
              key={h}
              className={'hole-tile' + (draft.holes === h ? ' active' : '')}
              onClick={() => set('holes')(h)}
            >
              <span className="num">{h}</span>
              <span className="label">Holes</span>
            </button>
          ))}
        </div>
      </section>

      {/* LOCATION */}
      <section className="setup-section">
        <div className="setup-label">Where are you going out?</div>
        <div className="location-actions">
          <button
            className={'pill-btn' + (draft.locationMode === 'search' ? ' active' : '')}
            onClick={() => set('locationMode')('search')}
          >
            🔍 Search
          </button>
          <button
            className={'pill-btn' + (draft.locationMode === 'near' ? ' active' : '')}
            onClick={() => set('locationMode')('near')}
          >
            📍 Near Me
          </button>
          <button
            className={'pill-btn' + (draft.locationMode === 'startBar' ? ' active' : '')}
            onClick={() => set('locationMode')('startBar')}
          >
            🍸 Start Bar
          </button>
        </div>
        <input
          className="setup-input"
          placeholder="City, neighborhood, or address"
          value={draft.location}
          onChange={(e) => set('location')(e.target.value)}
        />
        <div className="setup-helper">
          Be specific — neighborhood or district works best.
        </div>
      </section>

      {/* TRANSPORT */}
      <section className="setup-section">
        <div className="setup-label">Transport</div>
        <div className="pill-row">
          {TRANSPORT.map((t) => (
            <button
              key={t.id}
              className={'pill-btn' + (draft.transport === t.id ? ' active' : '')}
              onClick={() => set('transport')(t.id)}
            >
              <span className="emoji">{t.icon}</span> {t.label}
            </button>
          ))}
        </div>
      </section>

      {/* GROUP SIZE */}
      <section className="setup-section">
        <div className="setup-label">Group Size</div>
        <div className="pill-row">
          {GROUP_SIZES.map((g) => (
            <button
              key={g.id}
              className={'pill-btn' + (draft.groupSize === g.id ? ' active' : '')}
              onClick={() => set('groupSize')(g.id)}
            >
              {g.label}
            </button>
          ))}
        </div>
      </section>

      {/* START TIME */}
      <section className="setup-section">
        <div className="setup-label">Start Time</div>
        <div className="pill-row">
          {START_TIMES.map((t) => (
            <button
              key={t.id}
              className={'pill-btn' + (draft.startTime === t.id ? ' active' : '')}
              onClick={() => set('startTime')(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </section>

      {/* DRINK PREFERENCE */}
      <section className="setup-section">
        <div className="setup-label">Drink Preference</div>
        <div className="pill-row">
          {DRINKS.map((d) => (
            <button
              key={d.id}
              className={'pill-btn' + (draft.menuStyle === d.id ? ' active' : '')}
              onClick={() => set('menuStyle')(d.id)}
            >
              <span className="emoji">{d.icon}</span> {d.label}
            </button>
          ))}
        </div>
      </section>

      {/* BUDGET — premium feature, but the toggle is always visible */}
      <section className="setup-section">
        <div className="setup-label">Budget</div>
        <div className="pill-row">
          {BUDGETS.map((b) => (
            <button
              key={b.id}
              className={'pill-btn' + (draft.budget === b.id ? ' active' : '')}
              onClick={() => set('budget')(b.id)}
            >
              <span className="emoji">{b.icon}</span> {b.label}
            </button>
          ))}
        </div>
      </section>

      </div>
      <div className="setup-col">
      {/* TEAMS */}
      <section className="setup-section">
        <div className="setup-label-row">
          <div className="setup-label">Teams</div>
          <button className="pill-btn pill-btn-sm" onClick={addTeam}>
            + Add Team
          </button>
        </div>

        <div className="team-list">
          {teams.map((team) => (
            <TeamCard
              key={team.id}
              team={team}
              canRemove={teams.length > 1}
              onNameChange={(name) => updateTeam(team.id, { name })}
              onRemoveTeam={() => removeTeam(team.id)}
              onRenamePlayer={(i, name) => updatePlayerName(team.id, i, name)}
              onRemovePlayer={(i) => removePlayer(team.id, i)}
              onEnsureEmptyRow={() => appendEmptyPlayer(team.id)}
            />
          ))}
        </div>
      </section>

      {/* CUSTOM HOLES — only in custom mode */}
      {draft.mode === 'custom' && (
        <section className="setup-section">
          <div className="setup-label-row">
            <div className="setup-label">Holes</div>
            <button className="pill-btn pill-btn-sm" onClick={addCustomHole}>
              + Add Hole
            </button>
          </div>

          {customHoles.length === 0 ? (
            <button
              className="empty-holes-card"
              onClick={addCustomHole}
              type="button"
            >
              <div className="empty-holes-icon">⛳</div>
              <h4>Build your course</h4>
              <p>
                Add a hole for every bar on the route. Set the par, drop in the
                drink, and you're ready to tee off.
              </p>
              <span className="empty-holes-cta">+ Add your first hole</span>
            </button>
          ) : (
            <div className="custom-hole-list">
              {customHoles.map((hole, idx) => (
                <CustomHoleRow
                  key={hole.id}
                  index={idx}
                  total={customHoles.length}
                  hole={hole}
                  onChange={(patch) => updateCustomHole(hole.id, patch)}
                  onRemove={() => removeCustomHole(hole.id)}
                  onMoveUp={() => moveCustomHole(hole.id, -1)}
                  onMoveDown={() => moveCustomHole(hole.id, +1)}
                />
              ))}
              <button
                className="pill-btn pill-btn-sm add-hole-btn"
                onClick={addCustomHole}
              >
                + Add another hole
              </button>
            </div>
          )}
        </section>
      )}

      {/* CARD PENALTIES */}
      <section className="setup-section">
        <div className="setup-label-row">
          <div className="setup-label">Card Penalties</div>
          <button
            className={'pill-btn pill-btn-sm' + (draft.cardRules ? ' active' : '')}
            onClick={() => set('cardRules')(!draft.cardRules)}
            aria-pressed={draft.cardRules}
          >
            {draft.cardRules ? 'On' : 'Off'}
          </button>
        </div>

        <div className={'penalty-cards-grid' + (draft.cardRules ? '' : ' is-disabled')}>
          <PenaltyCard card={YELLOW_CARD} />
          <PenaltyCard card={RED_CARD} />
        </div>
      </section>

      </div>
      </div>

      {/* PREMIUM GATE — shown instead of an error when AI generation
          requires Premium. */}
      {premiumGate && (
        <div className="premium-gate">
          <div className="premium-gate-icon">👑</div>
          <h3>AI Course Generation requires Premium</h3>
          <p>
            Premium members get the AI Caddy — real bars, real photos, and
            optimized walking routes for any city in the world. One-time
            purchase, no subscription.
          </p>
          <button
            className="btn btn-primary btn-block btn-lg"
            onClick={() => navigate('/premium')}
          >
            👑 Unlock Premium — $4.99
          </button>
          <button
            className="btn btn-ghost btn-block"
            style={{ marginTop: '0.5rem' }}
            onClick={() => {
              updateDraft({ mode: 'custom' });
              setPremiumGate(false);
            }}
          >
            Build a custom course instead
          </button>
        </div>
      )}

      {error && !premiumGate && (
        <div className="card" style={{ color: 'var(--danger)', marginTop: '1rem' }}>
          {error}
        </div>
      )}

      {/* COURSE READY PREVIEW — only after generation succeeds */}
      {readyCourse && (
        <div className="ready-course">
          <div className="ready-course-header">
            <span className="ready-course-badge">COURSE READY</span>
            <span className="ready-course-code">{readyCourse.code}</span>
          </div>
          <h3 className="ready-course-title">
            {readyCourse.holes.length || draft.holes} holes
            {readyCourse.city ? ` in ${readyCourse.city}` : ''}
          </h3>
          {readyCourse.holes?.length > 0 && (
            <ul className="ready-course-list">
              {readyCourse.holes.map((h, i) => {
                const bar = h.name || h.bar || `Venue ${i + 1}`;
                const drink = h.drink || 'House special';
                const emoji = h.drinkEmoji || h.emoji || '🍸';
                const par = h.par || 4;
                const walk =
                  h.walkTime || h.walk_time || h.distance || '~2 min walk';
                return (
                  <li key={i} className="ready-course-item">
                    <span className="ready-course-num">{i + 1}</span>
                    <div className="ready-course-item-body">
                      <div className="ready-course-name">{bar}</div>
                      <div className="ready-course-meta">
                        <span>{emoji} {drink}</span>
                        <span className="text-muted"> · Par {par}</span>
                        {i < readyCourse.holes.length - 1 && (
                          <span className="text-muted"> · 🚶 {walk}</span>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="ready-course-actions">
            <button
              className="btn btn-ghost"
              onClick={() => setReadyCourse(null)}
              disabled={busy}
            >
              ↻ Regenerate
            </button>
          </div>
        </div>
      )}

      {/* Primary action — Generate first, then Start Round once ready.
          Only shown after the course is built (or, in AI mode, before
          generation as a CTA to generate). Custom mode requires at least
          one valid hole; AI mode requires a location. */}
      {!readyCourse ? (
        <button
          className="btn btn-primary btn-block btn-lg start-round-btn"
          onClick={handleGenerate}
          disabled={
            busy || premiumGate || (draft.mode === 'ai' ? !aiReady : !customReady)
          }
        >
          {busy
            ? draft.mode === 'ai'
              ? 'Generating course…'
              : 'Building course…'
            : draft.mode === 'ai'
              ? '✨ Generate Course'
              : '🛠️ Build Course'}
        </button>
      ) : (
        <button
          className="btn btn-primary btn-block btn-lg start-round-btn"
          onClick={handleStartRound}
        >
          <span style={{ marginRight: 6 }}>🏁</span> Start Round
        </button>
      )}
    </div>
  );
}

function CustomHoleRow({ index, total, hole, onChange, onRemove, onMoveUp, onMoveDown }) {
  return (
    <div className="custom-hole-card">
      <div className="custom-hole-head">
        <div className="custom-hole-num">{index + 1}</div>
        <input
          className="custom-hole-name"
          value={hole.name}
          placeholder="Bar name"
          onChange={(e) => onChange({ name: e.target.value })}
        />
        <button
          className="custom-hole-remove"
          onClick={onRemove}
          aria-label="Remove hole"
          type="button"
        >
          ×
        </button>
      </div>

      <div className="custom-hole-body">
        <input
          className="custom-hole-input"
          value={hole.address}
          placeholder="Address (optional)"
          onChange={(e) => onChange({ address: e.target.value })}
        />
        <div className="custom-hole-meta-row">
          <label className="custom-hole-par">
            <span>PAR</span>
            <select
              value={hole.par}
              onChange={(e) => onChange({ par: Number(e.target.value) })}
            >
              {[2, 3, 4, 5, 6].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
          <input
            className="custom-hole-input"
            value={hole.drink}
            placeholder="Drink (optional)"
            onChange={(e) => onChange({ drink: e.target.value })}
          />
        </div>
        {total > 1 && (
          <div className="custom-hole-reorder">
            <button
              className="reorder-btn"
              onClick={onMoveUp}
              disabled={index === 0}
              aria-label="Move up"
              type="button"
            >
              ↑
            </button>
            <button
              className="reorder-btn"
              onClick={onMoveDown}
              disabled={index === total - 1}
              aria-label="Move down"
              type="button"
            >
              ↓
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function PenaltyCard({ card }) {
  return (
    <div className={`penalty-card penalty-card-${card.type}`}>
      <div className="penalty-card-header">
        <FmCard type={card.type} size="lg" />
        <div className="penalty-card-title">
          <strong>{card.label}</strong>
          <span>{card.description}</span>
        </div>
      </div>
      <ul className="penalty-card-list">
        {card.infractions.map((reason) => (
          <li key={reason}>{reason}</li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Team card with an auto-growing player list.
 *
 * UX rules (per the spec):
 *   - Each team starts with one empty row.
 *   - Typing a name in a row auto-appends a fresh empty row below.
 *   - Enter / blur "commits" the row (the name was already saved on
 *     every keystroke, but Enter jumps focus to the next row).
 *   - Every row has an × remove button.
 *   - At least one row is always present, even if empty.
 *   - Team name is an inline editable input; tap to rename.
 */
function TeamCard({
  team,
  canRemove,
  onNameChange,
  onRemoveTeam,
  onRenamePlayer,
  onRemovePlayer,
  onEnsureEmptyRow,
}) {
  const players = team.players || [];

  // Ensure there is always exactly one trailing empty row so the user
  // can keep adding people without hunting for a button. We intentionally
  // depend only on the player count + last row's name (not the callback
  // which is re-created each parent render) to avoid an infinite loop.
  const lastName = players[players.length - 1]?.name || '';
  const needsEmpty =
    players.length === 0 || lastName.trim().length > 0;
  useEffect(() => {
    if (needsEmpty) onEnsureEmptyRow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsEmpty]);

  const handleKey = (e, i) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Move focus to the next row's input (or append+focus if last).
      const nextIdx = i + 1;
      if (nextIdx >= players.length) {
        onEnsureEmptyRow();
      }
      // Next paint — focus the freshly-rendered row.
      requestAnimationFrame(() => {
        const el = document.querySelector(
          `[data-team-id="${team.id}"] [data-player-idx="${nextIdx}"] input`
        );
        el?.focus();
      });
    }
  };

  return (
    <div className="team-card-setup" data-team-id={team.id}>
      <div className="team-card-header">
        <input
          className="team-name-input"
          value={team.name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="Team name"
          aria-label="Team name"
        />
        {canRemove && (
          <button
            className="team-remove"
            onClick={onRemoveTeam}
            aria-label="Remove team"
            type="button"
          >
            ×
          </button>
        )}
      </div>

      <div className="player-rows">
        {players.map((p, i) => {
          const initial = (p.name || '').trim()[0]?.toUpperCase() || '';
          const isEmpty = !(p.name || '').trim();
          return (
            <div
              key={p.id || i}
              className={'player-input-row' + (isEmpty ? ' is-empty' : '')}
              data-player-idx={i}
            >
              <div className="player-input-avatar">
                {initial || <span className="player-input-plus">+</span>}
              </div>
              <input
                type="text"
                value={p.name || ''}
                placeholder={isEmpty ? 'Add player…' : 'Name'}
                onChange={(e) => onRenamePlayer(i, e.target.value)}
                onKeyDown={(e) => handleKey(e, i)}
                autoComplete="off"
                spellCheck={false}
              />
              {/* Only show × on non-empty rows OR when there's more than
                  one row. Always keep at least one empty row visible. */}
              {(!isEmpty || players.length > 1) && (
                <button
                  type="button"
                  className="player-input-remove"
                  onClick={() => onRemovePlayer(i)}
                  aria-label="Remove player"
                  tabIndex={-1}
                >
                  ×
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
